/**
 * ApprovalsContext - Data store for page approval data
 *
 * This provider follows the pattern established in AnnotationsProvider and
 * documented in patterns/component-architecture.md:
 * - Separate read and write contexts to prevent unnecessary re-renders
 * - Server data managed by Apollo GraphQL (useQueryInterop)
 * - UI state managed by React Context
 *
 * Usage:
 *   const { approvalRequest, loading } = useApprovals();
 *   const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	type FC,
	type ReactNode,
} from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	ApprovalsContextCreateWorkflowApplicationMutation$data,
	ApprovalsContextCreateWorkflowApplicationMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowApplicationMutation.graphql.ts';
import type {
	ApprovalsContextCreateWorkflowReviewMutation$data,
	ApprovalsContextCreateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextUpdateReviewerDecisionMutation$data,
	ApprovalsContextUpdateReviewerDecisionMutation$variables,
} from './__generated__/ApprovalsContextUpdateReviewerDecisionMutation.graphql.ts';
import type {
	ApprovalsContextUpdateWorkflowReviewMutation$data,
	ApprovalsContextUpdateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextUpdateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextWorkflowStatusesQuery$data,
	ApprovalsContextWorkflowStatusesQuery$variables,
} from './__generated__/ApprovalsContextWorkflowStatusesQuery.graphql.ts';
// TODO: Uncomment after running Relay compiler for activeWorkflowApplicationQuery
// import type {
// 	ApprovalsContextActiveWorkflowApplicationQuery$data,
// 	ApprovalsContextActiveWorkflowApplicationQuery$variables,
// } from './__generated__/ApprovalsContextActiveWorkflowApplicationQuery.graphql.ts';

// GraphQL Mutations - AGG Ready
// Mutation: Create Workflow Application
const createWorkflowApplicationMutation = graphql`
	mutation ApprovalsContextCreateWorkflowApplicationMutation(
		$cloudId: ID!
		$createWorkflowApplicationRequestInput: ConfluenceCreateWorkflowApplicationRequestInput!
	) {
		confluence_createWorkflowApplication(
			cloudId: $cloudId
			createWorkflowApplicationRequestInput: $createWorkflowApplicationRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowStatus {
				id
				allowsReviews
				displayProperties {
					name
					description
					color
					icon
				}
			}
		}
	}
`;

// Mutation: Create Workflow Review
const createWorkflowReviewMutation = graphql`
	mutation ApprovalsContextCreateWorkflowReviewMutation(
		$cloudId: ID!
		$createContentApprovalRequestInput: ConfluenceCreateContentApprovalRequestInput!
	) {
		confluence_createContentApproval(
			cloudId: $cloudId
			createContentApprovalRequestInput: $createContentApprovalRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				workflowApplicationId
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// Mutation: Update Reviewer Decision (Approve/Deny)
const updateReviewerDecisionMutation = graphql`
	mutation ApprovalsContextUpdateReviewerDecisionMutation(
		$cloudId: ID!
		$confluenceUpdateReviewerDecisionInput: ConfluenceUpdateReviewerDecisionInput!
	) {
		confluence_updateReviewerDecision(
			cloudId: $cloudId
			confluenceUpdateReviewerDecisionInput: $confluenceUpdateReviewerDecisionInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// Mutation: Update Workflow Review (add/remove reviewers, update status)
const updateWorkflowReviewMutation = graphql`
	mutation ApprovalsContextUpdateWorkflowReviewMutation(
		$cloudId: ID!
		$updateContentApprovalGraphQLInput: ConfluenceUpdateContentApprovalRequestInput!
	) {
		confluence_updateContentApproval(
			cloudId: $cloudId
			updateContentApprovalGraphQLInput: $updateContentApprovalGraphQLInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// GraphQL Query - AGG Ready
// Query: Retrieve all available workflow statuses for given workflow id
const workflowStatusesQuery = graphql`
	query ApprovalsContextWorkflowStatusesQuery(
		$cloudId: ID!
		$transitionType: ConfluenceApprovalTransitionType
	) {
		confluence_contentApprovalStatuses(cloudId: $cloudId, transitionType: $transitionType) {
			id
			allowsReviews
			displayProperties {
				name
				description
				color
				icon
			}
		}
	}
`;

// GraphQL Query - For fetching active workflow application
// TODO: Uncomment when confluence_getActiveWorkflowApplication becomes AGG-ready
// Query: Retrieve Active Workflow Application for content
// This query gets the workflow application, which includes pendingWorkflowReview if available
// const activeWorkflowApplicationQuery = graphql`
// 	query ApprovalsContextActiveWorkflowApplicationQuery(
// 		$cloudId: ID!
// 		$workflowId: Long!
// 		$contentId: Long!
// 		$contentVersion: Int!
// 		$contentStatus: String!
// 	) {
// 		confluence_getActiveWorkflowApplication(
// 			cloudId: $cloudId
// 			workflowId: $workflowId
// 			contentId: $contentId
// 			contentVersion: $contentVersion
// 			contentStatus: $contentStatus
// 		) {
// 			success
// 			errors {
// 				message
// 				extensions {
// 					statusCode
// 					errorType
// 				}
// 			}
// 			workflowApplication {
// 				id
// 				workflowId
// 				contentId
// 				contentVersion
// 				contentStatus
// 				workflowStatusId
// 				isActive
// 				# TODO: Add pendingWorkflowReview field if available on ConfluenceWorkflowApplicationDetails
// 				# pendingWorkflowReview {
// 				#   id
// 				#   workflowApplicationId
// 				#   reviewStatus
// 				#   comment
// 				#   creationDate
// 				#   lastModifiedDate
// 				#   creator
// 				#   lastModifier
// 				#   reviewers {
// 				#     id
// 				#     reviewId
// 				#     principalType
// 				#     principalId
// 				#     isRequired
// 				#     user {
// 				#       id
// 				#       displayName
// 				#       photos {
// 				#         isPrimary
// 				#         value
// 				#       }
// 				#       emails {
// 				#         isPrimary
// 				#         value
// 				#       }
// 				#     }
// 				#     decision {
// 				#       id
// 				#       decisionStatus
// 				#       comment
// 				#       creationDate
// 				#       lastModifiedDate
// 				#       user {
// 				#         id
// 				#         displayName
// 				#         photos {
// 				#           isPrimary
// 				#           value
// 				#         }
// 				#         emails {
// 				#           isPrimary
// 				#           value
// 				#         }
// 				#       }
// 				#     }
// 				#   }
// 				# }
// 			}
// 		}
// 	}
// `;

// Types - TODO: Align with actual GraphQL schema
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'DECLINED' | 'CANCELLED';

export type Approver = {
	id: string;
	principalId: string;
	principalType: 'USER' | 'TEAM';
	name: string;
	avatarUrl?: string;
	email?: string;
	status: ApprovalStatus | null;
	message?: string;
	isRequired: boolean;
	respondedAt?: string;
};

export type ApprovalRequest = {
	id: string;
	contentId: string;
	requestorId: string;
	requestorName: string;
	requestorAvatarUrl?: string;
	message?: string;
	startedAt: string;
	status: ApprovalStatus;
	approvers: Approver[];
};

export type WorkflowStatus = {
	id: string;
	displayName: string;
	allowsReviews: boolean;
	displayProperties?: {
		name: string;
		description?: string;
		color?: string;
		icon?: string;
	};
};

export type ApprovalsContextValue = {
	// Query data
	approvalRequest: ApprovalRequest | null;
	// Available workflow statuses
	availableWorkflowStatuses: WorkflowStatus[];
	workflowStatusesLoading: boolean;

	// Computed values
	isCurrentUserRequestor: boolean;
	isCurrentUserApprover: boolean;
	currentUserApprover: Approver | null;
	approvalProgress: { current: number; total: number };

	// UI state
	isPaneOpen: boolean;
	setIsPaneOpen: (isOpen: boolean) => void;
};

export type StartApprovalVariables = {
	contentId: string;
	approvers: Array<{
		principalId: string;
		principalType: 'USER' | 'TEAM';
		isRequired: boolean;
	}>;
	message?: string;
};

export type UpdateApprovalStatusVariables = {
	reviewerId: string;
	status: 'APPROVED' | 'DECLINED';
	comment?: string;
};

export type AddApproverVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
	approvers: Array<{
		principalId: string;
		principalType: 'USER' | 'TEAM';
		isRequired: boolean;
	}>;
};

export type CancelApprovalVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
};

export type CreateWorkflowApplicationVariables = {
	contentId: string;
	contentStatus: 'CURRENT' | 'DRAFT';
	contentVersion: number;
	workflowId?: number;
	workflowStatusId?: number;
	isActive?: boolean;
};

export type ApprovalsDispatchContextValue = {
	// Mutations
	createWorkflowApplication: (variables: CreateWorkflowApplicationVariables) => Promise<void>;
	startApproval: (variables: StartApprovalVariables) => Promise<void>;
	updateApprovalStatus: (variables: UpdateApprovalStatusVariables) => Promise<void>;
	addApprover: (variables: AddApproverVariables) => Promise<void>;
	cancelApproval: (variables: CancelApprovalVariables) => Promise<void>;
};

// Default context values
const defaultContextValue: ApprovalsContextValue = {
	approvalRequest: null,
	availableWorkflowStatuses: [],
	workflowStatusesLoading: false,
	isCurrentUserRequestor: false,
	isCurrentUserApprover: false,
	currentUserApprover: null,
	approvalProgress: { current: 0, total: 0 },
	isPaneOpen: false,
	setIsPaneOpen: () => {
		throw new Error('setIsPaneOpen must be used within ApprovalsProvider');
	},
};

const defaultDispatchValue: ApprovalsDispatchContextValue = {
	createWorkflowApplication: async () => {
		throw new Error('createWorkflowApplication not implemented');
	},
	startApproval: async () => {
		throw new Error('startApproval not implemented');
	},
	updateApprovalStatus: async () => {
		throw new Error('updateApprovalStatus not implemented');
	},
	addApprover: async () => {
		throw new Error('addApprover not implemented');
	},
	cancelApproval: async () => {
		throw new Error('cancelApproval not implemented');
	},
};

// Create contexts
export const ApprovalsContext = createContext<ApprovalsContextValue>(defaultContextValue);
ApprovalsContext.displayName = 'ApprovalsContext';

export const ApprovalsDispatchContext =
	createContext<ApprovalsDispatchContextValue>(defaultDispatchValue);
ApprovalsDispatchContext.displayName = 'ApprovalsDispatchContext';

// Provider props
type ApprovalsProviderProps = {
	children: ReactNode;
};

/**
 * ApprovalsProvider
 *
 * Provides approval data and mutation functions to child components.
 *
 * This provider:
 * 1. Fetches approval data using GraphQL query
 * 2. Exposes data through read context (ApprovalsContext)
 * 3. Exposes mutations through write context (ApprovalsDispatchContext)
 * 4. Handles cache updates and refetching after mutations
 *
 * @example
 * ```tsx
 * <ApprovalsProvider>
 *   <BylineApprovalStatus />
 *   <ApprovalsPane />
 * </ApprovalsProvider>
 * ```
 */
export const ApprovalsProvider: FC<ApprovalsProviderProps> = ({ children }) => {
	const { cloudId, userId } = useSessionData();
	const isLicensed = useIsLicensed();
	// const [contentId] = usePageContentId();  TODO: Uncomment when contentId is needed

	const [isPaneOpen, setIsPaneOpen] = React.useState(false);

	// Query for available workflow statuses - AGG Ready
	const { data: workflowStatusesData, loading: workflowStatusesLoading } = useQueryInterop<
		ApprovalsContextWorkflowStatusesQuery$data,
		ApprovalsContextWorkflowStatusesQuery$variables
	>(workflowStatusesQuery, {
		variables: {
			cloudId,
			transitionType: null, // null returns all statuses
		},
		skip: !cloudId || !isLicensed || !expValEquals('confluence_approvals_eap', 'isEnabled', true),
	});

	// Transform workflow statuses data
	const availableWorkflowStatuses = useMemo(() => {
		if (!workflowStatusesData?.confluence_contentApprovalStatuses) return [];
		return workflowStatusesData.confluence_contentApprovalStatuses.reduce<WorkflowStatus[]>(
			(acc, status) => {
				if (status === null || status === undefined) return acc;
				acc.push({
					id: status.id || '',
					displayName: status.displayProperties?.name || '',
					allowsReviews: status.allowsReviews || false,
					displayProperties: status.displayProperties
						? {
								name: status.displayProperties.name || '',
								description: status.displayProperties.description || undefined,
								color: status.displayProperties.color || undefined,
								icon: status.displayProperties.icon || undefined,
							}
						: undefined,
				});
				return acc;
			},
			[],
		);
	}, [workflowStatusesData]);

	// Query for active workflow application
	// TODO: add query confluence_getActiveWorkflowApplication when itbecomes AGG-ready

	// TODO: Transform workflow application data to ApprovalRequest when query becomes available
	// The workflow application may include pendingWorkflowReview field
	// If not, we'll need to fetch it separately using workflowApplicationId

	// Temporary: approvalRequest is always null until confluence_getActiveWorkflowApplication is AGG-ready.
	const [mockData] = React.useState<ApprovalRequest | null>(null);

	// Transform query data - TODO: Update to transform actual GraphQL response when query is ready
	const approvalRequest = useMemo(() => {
		// TODO: Transform workflow application data when confluence_getActiveWorkflowApplication becomes available
		// const workflowApp = workflowApplication;
		// const review = workflowApp?.pendingWorkflowReview;
		// if (!review) return null;
		// return {
		//   id: review.id,
		//   contentId,
		//   requestorId: workflowApp.creator || '', // Get from workflow application
		//   requestorName: '', // TODO: Fetch user name from creator ID
		//   requestorAvatarUrl: undefined,
		//   message: review.comment || undefined,
		//   startedAt: review.creationDate || '',
		//   status: mapReviewStatusToApprovalStatus(review.reviewStatus),
		//   approvers: review.reviewers.map((reviewer) => ({
		//     id: reviewer.id,
		//     principalId: reviewer.principalId,
		//     principalType: reviewer.principalType === 'USER' ? 'USER' : 'TEAM',
		//     name: reviewer.user?.displayName || reviewer.principalId,
		//     avatarUrl: reviewer.user?.photos?.find((p) => p?.isPrimary)?.value,
		//     email: reviewer.user?.emails?.find((e) => e?.isPrimary)?.value,
		//     status: mapDecisionStatusToApprovalStatus(reviewer.decision?.decisionStatus || null),
		//     message: reviewer.decision?.comment || undefined,
		//     isRequired: reviewer.isRequired || false,
		//     respondedAt: reviewer.decision?.lastModifiedDate,
		//   })),
		// };

		return mockData;
	}, [mockData]);

	// Computed values
	const isCurrentUserRequestor = useMemo(() => {
		return approvalRequest?.requestorId === userId;
	}, [approvalRequest, userId]);

	const currentUserApprover = useMemo(() => {
		return approvalRequest?.approvers.find((approver) => approver.principalId === userId) || null;
	}, [approvalRequest, userId]);

	const approvalProgress = useMemo(() => {
		if (!approvalRequest) return { current: 0, total: 0 };
		const responded = approvalRequest.approvers.filter((a) => a.status !== null).length;
		return {
			current: responded,
			total: approvalRequest.approvers.length,
		};
	}, [approvalRequest]);

	// GraphQL Mutations
	const [createWorkflowApplicationMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowApplicationMutation$data,
		ApprovalsContextCreateWorkflowApplicationMutation$variables
	>(createWorkflowApplicationMutation, {
		// TODO: Add refetchQueries when activeWorkflowApplicationQuery is ready
		// refetchQueries: () => [{ query: activeWorkflowApplicationQuery, variables: { ... } }]
	});

	const [createWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowReviewMutation$data,
		ApprovalsContextCreateWorkflowReviewMutation$variables
	>(createWorkflowReviewMutation, {
		// TODO: Add refetchQueries when activeWorkflowApplicationQuery is ready
		// refetchQueries: () => [{ query: activeWorkflowApplicationQuery, variables: { ... } }]
	});

	const [updateReviewerDecisionMutationFn] = useMutationInterop<
		ApprovalsContextUpdateReviewerDecisionMutation$data,
		ApprovalsContextUpdateReviewerDecisionMutation$variables
	>(updateReviewerDecisionMutation, {
		// TODO: Add refetchQueries when activeWorkflowApplicationQuery is ready
		// refetchQueries: () => [{ query: activeWorkflowApplicationQuery, variables: { ... } }]
	});

	const [updateWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextUpdateWorkflowReviewMutation$data,
		ApprovalsContextUpdateWorkflowReviewMutation$variables
	>(updateWorkflowReviewMutation, {
		// TODO: Add refetchQueries when activeWorkflowApplicationQuery is ready
		// refetchQueries: () => [{ query: activeWorkflowApplicationQuery, variables: { ... } }]
	});

	// const [updateWorkflowReviewStatusMutation] = useMutationInterop<
	// 	ApprovalsContextUpdateWorkflowReviewStatusMutation$data,
	// 	ApprovalsContextUpdateWorkflowReviewStatusMutation$variables
	// >(updateWorkflowReviewStatusMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// const [addApproverToWorkflowReviewMutation] = useMutationInterop<
	// 	ApprovalsContextAddApproverToWorkflowReviewMutation$data,
	// 	ApprovalsContextAddApproverToWorkflowReviewMutation$variables
	// >(addApproverToWorkflowReviewMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// const [removeApproverFromWorkflowReviewMutation] = useMutationInterop<
	// 	ApprovalsContextRemoveApproverFromWorkflowReviewMutation$data,
	// 	ApprovalsContextRemoveApproverFromWorkflowReviewMutation$variables
	// >(removeApproverFromWorkflowReviewMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// const [cancelWorkflowReviewMutation] = useMutationInterop<
	// 	ApprovalsContextCancelWorkflowReviewMutation$data,
	// 	ApprovalsContextCancelWorkflowReviewMutation$variables
	// >(cancelWorkflowReviewMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// Wrapped mutation functions
	const createWorkflowApplication = useCallback(
		async (variables: CreateWorkflowApplicationVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			await createWorkflowApplicationMutationFn({
				variables: {
					cloudId,
					createWorkflowApplicationRequestInput: {
						contentId: parseInt(variables.contentId, 10), // API expects Long (number)
						contentStatus: variables.contentStatus,
						version: variables.contentVersion,
						workflowId: variables.workflowId ? variables.workflowId : undefined,
						workflowStatusId: variables.workflowStatusId ? variables.workflowStatusId : undefined,
						isActive: variables.isActive !== undefined ? variables.isActive : true,
					},
				},
			});
		},
		[cloudId, createWorkflowApplicationMutationFn],
	);

	const startApproval = useCallback(
		async (variables: StartApprovalVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			// TODO: Get actual content version and status
			// Options:
			// 1. Pass as props to ApprovalsProvider (contentVersion, contentStatus)
			// 2. Fetch from content query/context if available
			// 3. Use content-state package if available
			const contentVersion = 1; // TODO: Get from content data
			const contentStatus: 'CURRENT' | 'DRAFT' = 'CURRENT'; // TODO: Get from content data

			await createWorkflowReviewMutationFn({
				variables: {
					cloudId,
					createContentApprovalRequestInput: {
						contentId: parseInt(variables.contentId, 10), // API expects Long (number)
						contentStatus,
						version: contentVersion,
						comment: variables.message || null,
						reviewers: variables.approvers.map((approver) => ({
							principalId: approver.principalId,
							principalType: approver.principalType === 'TEAM' ? 'GROUP' : 'USER',
							isRequired: approver.isRequired,
						})),
					},
				},
			});
		},
		[cloudId, createWorkflowReviewMutationFn],
	);

	const updateApprovalStatus = useCallback(
		async (variables: UpdateApprovalStatusVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			const reviewerId = parseInt(variables.reviewerId, 10);

			let decisionStatus: 'REVIEWER_APPROVED' | 'REVIEWER_DENIED' | 'REVIEW_CANCELED';
			if (variables.status === 'APPROVED') {
				decisionStatus = 'REVIEWER_APPROVED';
			} else if (variables.status === 'DECLINED') {
				decisionStatus = 'REVIEWER_DENIED';
			} else {
				decisionStatus = 'REVIEW_CANCELED';
			}

			await updateReviewerDecisionMutationFn({
				variables: {
					cloudId,
					confluenceUpdateReviewerDecisionInput: {
						reviewerId,
						decisionStatus,
						comment: variables.comment || null,
					},
				},
			});
		},
		[cloudId, updateReviewerDecisionMutationFn],
	);

	const addApprover = useCallback(
		async (variables: AddApproverVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(variables.workflowApplicationId, 10),
						contentApprovalReviewId: parseInt(variables.contentApprovalReviewId, 10),
						reviewers: variables.approvers.map((approver) => ({
							principalId: approver.principalId,
							principalType: approver.principalType === 'TEAM' ? 'GROUP' : 'USER',
							isRequired: approver.isRequired,
						})),
					},
				},
			});
		},
		[cloudId, updateWorkflowReviewMutationFn],
	);

	const cancelApproval = useCallback(
		async (variables: CancelApprovalVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(variables.workflowApplicationId, 10),
						contentApprovalReviewId: parseInt(variables.contentApprovalReviewId, 10),
						approvalStatus: 'CANCELED',
					},
				},
			});
		},
		[cloudId, updateWorkflowReviewMutationFn],
	);

	// Context values - memoized to prevent unnecessary re-renders
	const contextValue = useMemo<ApprovalsContextValue>(
		() => ({
			approvalRequest,
			availableWorkflowStatuses,
			workflowStatusesLoading,
			isCurrentUserRequestor,
			isCurrentUserApprover: !!currentUserApprover,
			currentUserApprover,
			approvalProgress,
			isPaneOpen,
			setIsPaneOpen,
		}),
		[
			approvalRequest,
			availableWorkflowStatuses,
			workflowStatusesLoading,
			isCurrentUserRequestor,
			currentUserApprover,
			approvalProgress,
			isPaneOpen,
		],
	);

	const dispatchValue = useMemo<ApprovalsDispatchContextValue>(
		() => ({
			createWorkflowApplication,
			startApproval,
			updateApprovalStatus,
			addApprover,
			cancelApproval,
		}),
		[createWorkflowApplication, startApproval, updateApprovalStatus, addApprover, cancelApproval],
	);

	return (
		<ApprovalsContext.Provider value={contextValue}>
			<ApprovalsDispatchContext.Provider value={dispatchValue}>
				{children}
			</ApprovalsDispatchContext.Provider>
		</ApprovalsContext.Provider>
	);
};

/**
 * Hook to access approval data (read-only)
 *
 * @example
 * ```tsx
 * const { approvalRequest, loading, approvalProgress } = useApprovals();
 * ```
 */
export const useApprovals = (): ApprovalsContextValue => {
	return useContext(ApprovalsContext);
};

/**
 * Hook to access approval mutations and actions (write-only)
 *
 * @example
 * ```tsx
 * const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 * ```
 */
export const useApprovalsDispatch = (): ApprovalsDispatchContextValue => {
	return useContext(ApprovalsDispatchContext);
};
